package br.com.heyagenda.domain.model

import br.com.heyagenda.domain.util.getPlatformCurrentDateTime

data class CalendarDayItem(
    val dateIso: String,
    val dayOfMonth: Int,
    val weekdayShort: String,
    val monthShort: String,
    val isToday: Boolean,
    val label: String
)

object CalendarUtils {
    fun getShortWeekday(dayOfWeek: DayOfWeekName): String = dayOfWeek.shortLabel

    fun getShortMonth(monthNumber: Int): String = when (monthNumber) {
        1 -> "Jan"
        2 -> "Fev"
        3 -> "Mar"
        4 -> "Abr"
        5 -> "Mai"
        6 -> "Jun"
        7 -> "Jul"
        8 -> "Ago"
        9 -> "Set"
        10 -> "Out"
        11 -> "Nov"
        12 -> "Dez"
        else -> ""
    }

    fun getMonthFullName(monthNumber: Int): String = when (monthNumber) {
        1 -> "janeiro"
        2 -> "fevereiro"
        3 -> "março"
        4 -> "abril"
        5 -> "maio"
        6 -> "junho"
        7 -> "julho"
        8 -> "agosto"
        9 -> "setembro"
        10 -> "outubro"
        11 -> "novembro"
        12 -> "dezembro"
        else -> ""
    }

    fun getMonthShortAbbr(monthNumber: Int): String = when (monthNumber) {
        1 -> "jan."
        2 -> "fev."
        3 -> "mar."
        4 -> "abr."
        5 -> "mai."
        6 -> "jun."
        7 -> "jul."
        8 -> "ago."
        9 -> "set."
        10 -> "out."
        11 -> "nov."
        12 -> "dez."
        else -> ""
    }

    // Sakamoto's algorithm: 0=Sunday, 1=Monday, ..., 6=Saturday
    fun getDayOfWeek(year: Int, month: Int, day: Int): DayOfWeekName {
        val t = intArrayOf(0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4)
        var y = year
        if (month < 3) y -= 1
        val d = (y + y / 4 - y / 100 + y / 400 + t[month - 1] + day) % 7
        return when (d) {
            0 -> DayOfWeekName.SUNDAY
            1 -> DayOfWeekName.MONDAY
            2 -> DayOfWeekName.TUESDAY
            3 -> DayOfWeekName.WEDNESDAY
            4 -> DayOfWeekName.THURSDAY
            5 -> DayOfWeekName.FRIDAY
            6 -> DayOfWeekName.SATURDAY
            else -> DayOfWeekName.MONDAY
        }
    }

    fun getDayOfWeekForIso(dateIso: String): DayOfWeekName {
        return try {
            val parts = dateIso.split("-")
            getDayOfWeek(parts[0].toInt(), parts[1].toInt(), parts[2].toInt())
        } catch (_: Throwable) {
            val now = getPlatformCurrentDateTime()
            getDayOfWeek(now.year, now.monthNumber, now.dayOfMonth)
        }
    }

    fun daysInMonth(year: Int, month: Int): Int {
        return when (month) {
            1, 3, 5, 7, 8, 10, 12 -> 31
            4, 6, 9, 11 -> 30
            2 -> if ((year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)) 29 else 28
            else -> 30
        }
    }

    fun getTodayIso(): String = getPlatformCurrentDateTime().dateIso

    fun getTodayDate(): String = getTodayIso()

    fun getFormattedToday(): String {
        val dt = getPlatformCurrentDateTime()
        val dow = getDayOfWeek(dt.year, dt.monthNumber, dt.dayOfMonth)
        val monthStr = getMonthShortAbbr(dt.monthNumber)
        return "${dow.label}, ${dt.dayOfMonth} $monthStr"
    }

    fun getNextDays(daysCount: Int = 14): List<CalendarDayItem> {
        val now = getPlatformCurrentDateTime()
        var y = now.year
        var m = now.monthNumber
        var d = now.dayOfMonth

        val items = mutableListOf<CalendarDayItem>()
        for (offset in 0 until daysCount) {
            val isToday = offset == 0
            val dow = getDayOfWeek(y, m, d)
            val monthStr = getShortMonth(m)
            val dateIso = "$y-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
            items.add(
                CalendarDayItem(
                    dateIso = dateIso,
                    dayOfMonth = d,
                    weekdayShort = dow.shortLabel,
                    monthShort = monthStr,
                    isToday = isToday,
                    label = "$d ${dow.shortLabel}"
                )
            )

            d++
            if (d > daysInMonth(y, m)) {
                d = 1
                m++
                if (m > 12) {
                    m = 1
                    y++
                }
            }
        }
        return items
    }

    fun addDays(year: Int, month: Int, day: Int, delta: Int): Triple<Int, Int, Int> {
        var y = year
        var m = month
        var d = day + delta

        while (d < 1) {
            m--
            if (m < 1) {
                m = 12
                y--
            }
            d += daysInMonth(y, m)
        }

        while (d > daysInMonth(y, m)) {
            d -= daysInMonth(y, m)
            m++
            if (m > 12) {
                m = 1
                y++
            }
        }

        return Triple(y, m, d)
    }

    fun formatIso(year: Int, month: Int, day: Int): String {
        return "$year-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
    }

    fun getStartOfWeekIso(): String {
        val now = getPlatformCurrentDateTime()
        val dow = getDayOfWeek(now.year, now.monthNumber, now.dayOfMonth)
        val daysFromMonday = when (dow) {
            DayOfWeekName.MONDAY -> 0
            DayOfWeekName.TUESDAY -> 1
            DayOfWeekName.WEDNESDAY -> 2
            DayOfWeekName.THURSDAY -> 3
            DayOfWeekName.FRIDAY -> 4
            DayOfWeekName.SATURDAY -> 5
            DayOfWeekName.SUNDAY -> 6
        }
        val (y, m, d) = addDays(now.year, now.monthNumber, now.dayOfMonth, -daysFromMonday)
        return formatIso(y, m, d)
    }

    fun getEndOfWeekIso(): String {
        val now = getPlatformCurrentDateTime()
        val dow = getDayOfWeek(now.year, now.monthNumber, now.dayOfMonth)
        val daysToSunday = when (dow) {
            DayOfWeekName.MONDAY -> 6
            DayOfWeekName.TUESDAY -> 5
            DayOfWeekName.WEDNESDAY -> 4
            DayOfWeekName.THURSDAY -> 3
            DayOfWeekName.FRIDAY -> 2
            DayOfWeekName.SATURDAY -> 1
            DayOfWeekName.SUNDAY -> 0
        }
        val (y, m, d) = addDays(now.year, now.monthNumber, now.dayOfMonth, daysToSunday)
        return formatIso(y, m, d)
    }

    fun getStartOfMonthIso(): String {
        val now = getPlatformCurrentDateTime()
        return formatIso(now.year, now.monthNumber, 1)
    }

    fun getEndOfMonthIso(): String {
        val now = getPlatformCurrentDateTime()
        val lastDay = daysInMonth(now.year, now.monthNumber)
        return formatIso(now.year, now.monthNumber, lastDay)
    }

    fun getDaysAgoIso(daysAgo: Int): String {
        val now = getPlatformCurrentDateTime()
        val (y, m, d) = addDays(now.year, now.monthNumber, now.dayOfMonth, -daysAgo)
        return formatIso(y, m, d)
    }

    fun formatIsoToBr(dateIso: String?): String {
        if (dateIso.isNullOrBlank()) return ""
        val parts = dateIso.trim().split("-")
        return if (parts.size == 3) {
            val y = parts[0]
            val m = parts[1].padStart(2, '0')
            val d = parts[2].padStart(2, '0')
            "$d/$m/$y"
        } else {
            dateIso
        }
    }

    fun formatBrToIso(brDate: String?): String {
        if (brDate.isNullOrBlank()) return ""
        val parts = brDate.trim().split("/")
        return if (parts.size == 3) {
            val d = parts[0].padStart(2, '0')
            val m = parts[1].padStart(2, '0')
            val y = parts[2]
            "$y-$m-$d"
        } else {
            brDate
        }
    }
}
